import { Services } from '../services'

export type JhaResponse = 'Compliant' | 'Non-Compliant' | 'N/A' | ''
export type JhaCorrected = 'Yes' | 'No' | ''
export type JhaStatus = 'Draft' | 'Submitted'

export interface JhaCorrectionHistoryEntry {
    id: string
    eventType: string
    actorUserId: string
    actorHrRef: number | null
    comment: string
    createdOn: string | Date
}

function newGuid(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
              const r = (Math.random() * 16) | 0
              const v = c === 'x' ? r : (r & 0x3) | 0x8
              return v.toString(16)
          })
}

export class JhaChecklistItem {
    id = ''
    sortOrder = 0
    question = ''
    response: JhaResponse = ''
    comments = ''
    corrected: JhaCorrected = ''
    correctedByUserId: string | null = null
    correctedOn: Date | null = null
    correctionComments = ''
    imageUrls: string[] = []
    imageFileNames: string[] = []
    correctionHistory: JhaCorrectionHistoryEntry[] = []
}

export class JhaSection {
    id = ''
    sortOrder = 0
    name = ''
    notApplicable = false
    items: JhaChecklistItem[] = []
    comments = ''

    static create(name: string, sortOrder: number, questions: string[]): JhaSection {
        const section = new JhaSection()
        section.id = newGuid()
        section.sortOrder = sortOrder
        section.name = name
        section.items = questions.map((q, i) => {
            const item = new JhaChecklistItem()
            item.id = newGuid()
            item.sortOrder = i
            item.question = q
            return item
        })
        return section
    }
}

export class JobHazardAnalysis {
    id = ''
    indexId = 0
    date: Date = new Date()
    job = ''
    jobSiteLocation = ''
    inspectorHrRef: number | null = null
    inspectorVendorId: number | null = null

    requiresSign: JhaResponse = ''
    weeklyToolboxTopic: JhaResponse = ''
    formsNeeded = ''
    description = ''

    sections: JhaSection[] = []

    subcontractors = ''
    correctiveActions = ''
    notes = ''
    generalImageUrls: string[] = []
    generalImageFileNames: string[] = []
    signedBy = ''
    signedOn: Date | null = null

    status: JhaStatus = 'Draft'
    submittedOn: Date | null = null
    cumulativeLaborHoursAtSubmission: number | null = null
    createdOn: Date = new Date()
    createdByUserId: string | null = null
    archived = false

    // Set by the API when a Safety Feedback record triggered this JHA (read-only).
    triggeredBySafetyFeedbackId: string | null = null
    triggeredBySafetyFeedbackIndexId: number | null = null

    // Computed display fields populated by $setProperties.
    jobName = ''
    inspectorName = ''
    nonCompliantCount = 0
    nonCompliantOpenCount = 0

    static createNew(): JobHazardAnalysis {
        const jha = new JobHazardAnalysis()
        // Generate the root Guid client-side. Empty-string IDs fail the server-side Guid binder
        // and cause the controller to receive a null DTO. Sections/items follow the same pattern
        // (see JhaSection.create) so item IDs are stable across the save -> photo-upload flow.
        jha.id = newGuid()
        jha.sections = JobHazardAnalysis.buildSections()
        return jha
    }

    /**
     * Returns a parsed list of subcontractor names. The server stores subcontractors as a single
     * pipe-delimited string for simplicity (no separate child table).
     */
    get subcontractorList(): string[] {
        if (!this.subcontractors) return []
        return this.subcontractors.split('|').map(s => s.trim()).filter(s => s.length > 0)
    }

    set subcontractorList(values: string[]) {
        this.subcontractors = (values ?? []).map(v => v.trim()).filter(v => v.length > 0).join('|')
    }

    protected $setProperties(source: any, services: Services): void {
        let jobName = ''
        if (source.job && services.data.vpJobs) {
            const match = services.data.vpJobs.items.filter(x => x.id === source.job)
            if (match.length > 0) jobName = match[0].name
        }
        source.jobName = jobName

        // inspectorName is filled in by the list component since there's no vpUsers repo.
        source.inspectorName = source.inspectorName ?? ''

        let nonCompliant = 0
        let nonCompliantOpen = 0
        for (const section of source.sections ?? []) {
            for (const item of section.items ?? []) {
                if (item.response === 'Non-Compliant') {
                    nonCompliant++
                    if (item.corrected !== 'Yes') nonCompliantOpen++
                }
            }
        }
        source.nonCompliantCount = nonCompliant
        source.nonCompliantOpenCount = nonCompliantOpen
    }

    static buildSections(): JhaSection[] {
        const defs: { name: string; questions: string[] }[] = [
            {
                name: 'Personal Protective Equipment (PPE)',
                questions: [
                    'PPE is being worn by all VanCon Employees?',
                    'Subcontractors onsite are wearing PPE?',
                    'Other PPE such as Specialty Gloves, Face Shields, Hearing protection, Respiratory Protection, etc. being worn to protect workers?',
                ],
            },
            {
                name: 'Employee Health/First Aid',
                questions: [
                    'Emergency Information Provided?',
                    'Are First Aid Kits Available?',
                    'Are Toilets available for use?',
                ],
            },
            {
                name: 'Housekeeping / Specific Site Conditions',
                questions: [
                    'Is the jobsite neat, orderly, walkways clear, materials stacked?',
                    'Are dumpsters on site?',
                    'Open holes protected, marked and secured?',
                    'Slip, trip and fall hazards protected?',
                    'Impalement hazards protected?',
                    'Temp Fencing, Danger Tape, Boards placed to protect Trenches or Open Holes?',
                    'Personal Vehicles are parked offsite?',
                    'Have Spills been cleaned up?',
                ],
            },
            {
                name: 'Hand / Power / Pneumatic Tools',
                questions: [
                    'Guards are in place on all Hand, Power Tools that require a guard?',
                    'Pneumatic air tools have safety clips/retainers installed?',
                    'Chop saws have the guard in place; right blade and dust control measures are in place?',
                    'Corded Tools - Checked for cuts in cord or exposed wires?',
                    'Tools are not modified for use?',
                    'Compressor hoses have whip checks installed.',
                ],
            },
            {
                name: 'HAZ COM',
                questions: [
                    'Fuels, Aerosols, Solvents are stored properly?',
                    'Portable Fuel Containers are metal? (Red, Yellow)',
                    'Large Fuel Containers have Placards?',
                    'Fire Extinguishers are present on work site for extinguishment of Fire?',
                    'Fire Extinguishers have Labels?',
                    'Spill Kits provided for Spills?',
                ],
            },
            {
                name: 'Trench & Excavation',
                questions: [
                    'Competent Person on Site?',
                    'Sloping, Shoring or Benching is present?',
                    'Trench Plates if used are installed and secured correctly?',
                    'Access and Egress provided for workers in trenches deeper than 4 feet?',
                    'Ladders extend 3 feet above ground level?',
                    'Ladders are within 25 feet of employees working in trench?',
                    'Fall Protection provided while working around trenches greater than 6\' in depth.',
                    'Spoil piles, Equipment and Materials are at least 2\' minimum from edge of trench/excavation?',
                ],
            },
            {
                name: 'Slide Rail (Trench & Excavation)',
                questions: [
                    'Slide Rail System is installed properly for protection of employees?',
                    'Cat Walks are being used to provide access to trench?',
                    'Ladders are extended 3\' from ground level for access and egress from trench?',
                    'Straps are installed to provide Fall Protection?',
                    'Chains installed correctly to secure side panels?',
                    'Chains have tags attached?',
                    'Material is placed on sides of Slide Rail panels?',
                    'Pike poles are present to help set the panels in the H braces?',
                    'Tag lines are used to support panels as they are flown in to be set?',
                ],
            },
            {
                name: 'Trench Boxes (Trench & Excavation)',
                questions: [
                    'Trench Boxes installed in trench correctly?',
                    'Trench Box installed has a maximum of 2\' from bottom of trench to bottom of box?',
                    'Picking eyes on Aluminum boxes are present for lifting?',
                    'All pins and keepers in place for boxes?',
                    'Ladders are inside the boxes for access & egress?',
                    'Ladders are tied off?',
                    'Ladders extend 3\' from top of box or ground level per standard?',
                    'Cat walks in place for access to the trench?',
                ],
            },
            {
                name: 'Confined Space Entry',
                questions: [
                    'Has local Fire & Rescue been notified of work being done in area?',
                    'Competent Person onsite to fill out the Permit? Permit Completed?',
                    'Tripod in use? Set up correctly?',
                    'Communication has been established?',
                    'Type of Confined Space? (Manhole, Pipeline, etc.)',
                    'Top Attendant Present?',
                    'Is 4 gas monitoring and recording taking place?',
                    '4 Gas Monitor has been bump tested prior to use and calibrated regularly?',
                    'Ventilation (if needed) is in place prior to working in Confined Space?',
                    'Access and Egress established?',
                    'Adequate Lighting is available for work being done?',
                    'Attendants are identified and know their role?',
                    'Entrants identified and know their role?',
                    'Rescue Plan explained and understood?',
                    'Did we reclassify the confined space to NON permit required?',
                ],
            },
            {
                name: 'Fall Protection',
                questions: [
                    'Employees wearing harnesses are wearing them correctly?',
                    'Handrail if installed is maintained?',
                    'Personal fall arrest systems in use? (Yo-yo, Lanyard, SRL)',
                    'Horizontal Lifelines in use?',
                    'Ladders used are set up correctly, tied down and inspected periodically for defects?',
                    'Ladders are installed within 25\' of employees?',
                    'Leading edges are identified and protections in place to protect workers from a fall greater than 6\'?',
                    'Roofers are tied off and protected from a fall greater than 6 feet?',
                    'Subcontractors are protected from a Fall Hazard?',
                ],
            },
            {
                name: 'Working/Walking Surfaces',
                questions: [
                    'Walking and working surfaces are free of hazards such as sharp or protruding objects, loose boards, corrosion, leaks, spills?',
                    'Employees are provided with a safe means of access and egress to and from working surfaces?',
                    'Open holes are covered correctly? (Board or Lid with HOLE painted on it and secured)',
                    'If stairways exist on site they are free of slip, trip and fall hazards?',
                ],
            },
            {
                name: 'Electrical',
                questions: [
                    'GFCI on generators, light plants, back of pickups function correctly?',
                    'Extension cords are free from cuts, abrasions, or wire exposures, ends are not missing grounding prong, or damaged?',
                    'Extension cord plugs are protected from weather?',
                    'Panels, power boxes have covers and remained closed?',
                    '3 Foot perimeter in front of electrical panel is clear and maintained?',
                    'Whisper watts are grounded or bonded if applicable to size?',
                    'Overhead Powerlines are identified and warning signs are up?',
                ],
            },
            {
                name: 'Lock Out/Tag Out',
                questions: [
                    'Electrical Panels that are LOTO are monitored when work is being performed?',
                    'Tags and Locks are identifiable? Have been filled out properly if in use?',
                    'Employees directly affected by the LOTO have been made aware and understand LOTO Procedures?',
                    'Isolation of energy source shut down and verified?',
                    'Heavy Equipment has been properly locked out / tagged out and communicated?',
                    'Red Tags placed on items needing to be serviced?',
                    'Lock out tag out kits available?',
                ],
            },
            {
                name: 'Scaffolding',
                questions: [
                    'Scaffolding assembled correctly?',
                    'Daily inspection tag present and signed?',
                    'Scaffolding fully planked per OSHA standard?',
                    'Planks are secured to prevent movement?',
                    'Guardrails are used if work height is >6 feet?',
                    'Guardrails installed correctly per OSHA standard? (38-42;21)',
                    'End bracing in place to protect workers from fall?',
                    'Planks don\'t extend longer than 12"?',
                    'Access Ladders are secured to the scaffolding?',
                    'Railing installed on the ends of cat walks?',
                    'Footings are level, sound, and rigid?',
                    'Working surfaces clear of debris & other slip, trip & fall hazards?',
                    'Mudsills are installed correctly?',
                    'Base plates are installed and screwed or nailed to mudsills?',
                    'Cross bracing is installed in all bays?',
                    'Out Riggers are attached to Baker Scaffold if height exceeds 10\'?',
                    'Fall Protection is in place around top sections of Scaffolds?',
                    'Coupling Pins are installed with keepers?',
                ],
            },
            {
                name: 'Concrete Items',
                questions: [
                    'Rebar Caps are in place on all rebar, stakes longer than 1.5 inches?',
                    'Grinding, Cutting and dry cutting is being done with proper PPE protocols?',
                    'Harnesses fit and are worn properly?',
                    'Boards, nails, screws are removed or bent over to keep from being stepped on?',
                    'Items being transported on trucks are properly secured.',
                    'Blankets - folded up / installed and secured from blowing away?',
                    'Suspended loads are identified, and employees are not walking under load?',
                    'Forms being packed by employees are using proper lifting techniques?',
                    'Buckets used to grout are approved buckets?',
                    'Ladders, stairs or access points are set up properly?',
                ],
            },
            {
                name: 'Rigging / Harnesses',
                questions: [
                    'Chains, Slings, Tag lines, Lifting Hooks, Shackles, Cables inspected and replaced when bad?',
                    'Tag Lines are present and inspected for wear and tear?',
                    'PPE while rigging up equipment?',
                    'Lifting hooks are not bent, missing triggers, or broken?',
                    'Two ways, four ways are being used to pick up trench boxes, panels, etc?',
                    'Harnesses are inspected regularly for defects?',
                    'Harnesses have been sized to fit the employee?',
                ],
            },
            {
                name: 'Welding Cylinders',
                questions: [
                    'Oxygen & Acetylene Cylinders properly separated and stored in identified containers?',
                    'All cylinders are stored upright and secured in trucks, on trailers or in containers?',
                    'Hoses & Torches free from defects.',
                    'Torch Regulators and Gauges in good condition?',
                    'Torches have flash arrestors or Check Valves?',
                ],
            },
            {
                name: 'Welding/Cutting',
                questions: [
                    'Hot work permit filled out?',
                    'Fire protection in place?',
                    'Cylinders used are stored upright?',
                    'Flammables are stored away from heat source?',
                    'Starters are spark triggers? (No Lighters or oxygen to start torches)',
                    'Tools, hoses are in good working condition?',
                ],
            },
            {
                name: 'Traffic Control',
                questions: [
                    'Traffic control barriers, signs, signals set up according to MUTCD standards?',
                    'Flaggers used are certified?',
                    'Workers are protected from traffic while work is being done?',
                    'Proper PPE is being worn during day shift and night work?',
                ],
            },
            {
                name: 'Equipment',
                questions: [
                    'Operators demonstrate competency?',
                    'Fire Extinguishers are installed?',
                    'Is there leaks on the equipment that is visible?',
                    'Are back up alarms working?',
                ],
            },
        ]
        return defs.map((d, i) => JhaSection.create(d.name, i, d.questions))
    }
}
